import { useMemo } from 'react';
import { type EntitySuggestion } from '@bliks-io/contracts';
import type { SuccessorChooserKey } from '@/lib/successorEntityMapping';
import { usePersonPickerConfig } from '../hooks/usePersonPickerConfig';
import {
  useOrgFunctionPickerConfig,
  type OrgFunctionPickerRequestContext
} from '../hooks/useOrgFunctionPickerConfig';
import {
  useSystemPickerConfig,
  type SystemPickerRequestContext
} from '../hooks/useSystemPickerConfig';
import { useExternalContactPickerConfig } from '../hooks/useExternalContactPickerConfig';
import { recommendationRecordingId } from '../shared/recommendationScope';

interface UseSuccessorPickerConfigsArgs {
  open: boolean;
  currentType: SuccessorChooserKey | null;
  processId?: string;
  recordingId?: string;
  /** Start event node behind the starter picker — see the config hooks. */
  startEventNodeId?: string;
  /** Draft-editor host: create/link post to the draft suggestion routes. */
  draftProcessId?: string;
  sourceRuleId?: string;
  isAssigneeMode: boolean;
  pendingSuggestions?: EntitySuggestion[];
  allowCreate?: boolean;
}

export function useSuccessorPickerConfigs({
  open,
  currentType,
  processId,
  recordingId,
  startEventNodeId,
  draftProcessId,
  sourceRuleId,
  isAssigneeMode,
  pendingSuggestions,
  allowCreate
}: UseSuccessorPickerConfigsArgs) {
  // Team and role phases share one request context — both write the same
  // unified orgFunctions slot, the split is presentation-only (ADR 0004).
  const orgFunctionRequestContext: OrgFunctionPickerRequestContext | undefined = useMemo(
    () =>
      recordingId
        ? {
            sourceStepRecordingId: recordingId,
            sourceStepRecordingPlace: isAssigneeMode ? 'assigned_to' : 'successor_org_function',
            sourceRuleId
          }
        : undefined,
    [recordingId, isAssigneeMode, sourceRuleId]
  );

  const systemRequestContext: SystemPickerRequestContext | undefined = useMemo(
    () =>
      recordingId
        ? {
            sourceStepRecordingId: recordingId,
            sourceStepRecordingPlace: 'successor_system',
            sourceRuleId
          }
        : undefined,
    [recordingId, sourceRuleId]
  );

  const recommendationScopeId = recommendationRecordingId(recordingId, draftProcessId);

  const shared = {
    processId,
    recordingId: recommendationScopeId,
    startEventNodeId,
    draftProcessId,
    selectedIds: [],
    pendingSuggestions,
    allowCreate
  };

  const personHook = usePersonPickerConfig({
    open: open && currentType === 'person',
    mode: 'single',
    processId,
    recordingId: recommendationScopeId
  });
  // One kind-following config serves both team and role phases — see
  // HelpersEntityPickerModal.tsx for why.
  const orgFunctionHook = useOrgFunctionPickerConfig({
    ...shared,
    open: open && (currentType === 'team' || currentType === 'role'),
    mode: 'single',
    kind: currentType === 'role' ? 'role' : 'team',
    requestContext: orgFunctionRequestContext
  });
  const systemHook = useSystemPickerConfig({
    ...shared,
    open: open && currentType === 'system',
    mode: 'single',
    requestContext: systemRequestContext
  });
  const externalContactHook = useExternalContactPickerConfig({
    open: open && currentType === 'externalContact',
    mode: 'single',
    processId,
    recordingId: recommendationScopeId
  });

  return { personHook, orgFunctionHook, systemHook, externalContactHook };
}
